import React, { useState } from 'react';
import { PublishedPost, formatRelativeTime } from '../hooks/usePublishedPosts';
import { platforms } from '../config/platforms';
import { PlatformIcon } from './PlatformIcon';
import './PostCard.css';

const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
    <line x1="10" y1="11" x2="10" y2="17"></line>
    <line x1="14" y1="11" x2="14" y2="17"></line>
  </svg>
);

const RetryIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10"></polyline>
    <polyline points="1 20 1 14 7 14"></polyline>
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"></path>
    <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"></path>
  </svg>
);

interface PostCardProps {
  post: PublishedPost;
  variant?: 'compact' | 'full'; // compact for LinkedPostBar, full for Posted tab
  showActions?: boolean;
  onReply?: (post: PublishedPost) => void;
  onQuote?: (post: PublishedPost) => void;
  onDelete?: (post: PublishedPost) => void;
  isThreaded?: boolean; // if true, show user icon instead of profile pic
}

interface PostAvatarProps {
  firstPlatform: 'bluesky' | 'farcaster' | null;
  profilePicUrl: string | null;
  isThreaded: boolean;
}

const PostAvatar: React.FC<PostAvatarProps> = ({ firstPlatform, profilePicUrl, isThreaded }) => {
  if (!firstPlatform && !isThreaded) return null;

  const platformTitle = firstPlatform ? `Posted to ${platforms[firstPlatform].name}` : 'Posted post';
  const avatarAlt = firstPlatform ? `${platforms[firstPlatform].name} profile` : 'Post author';

  if (!isThreaded && profilePicUrl) {
    return (
      <img
        src={profilePicUrl}
        alt={avatarAlt}
        className="post-card-avatar"
        title={platformTitle}
      />
    );
  }

  return (
    <div
      className="post-card-avatar post-card-avatar-threaded"
      title={platformTitle}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
        <circle cx="12" cy="7" r="4"></circle>
      </svg>
    </div>
  );
};

export const PostCard: React.FC<PostCardProps> = ({
  post,
  variant = 'full',
  showActions = false,
  onReply,
  onQuote,
  onDelete,
  isThreaded = false,
}) => {
  const [copied, setCopied] = useState(false);

  // Get first platform for profile pic (Bluesky or Farcaster only, skip Twitter)
  const firstPlatform = post.bluesky ? 'bluesky' : post.farcaster ? 'farcaster' : null;
  const profilePicUrl = post.profilePicUrl || null;
  const deleteLabel = post.hidden ? 'Retry delete' : 'Delete post';

  // Truncate content based on variant
  const displayContent = variant === 'compact'
    ? (post.content.length > 100 ? post.content.slice(0, 100) + '...' : post.content)
    : post.content;

  const handleCopy = async () => {
    if (!navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(post.content);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      console.error('Failed to copy post text:', error);
    }
  };

  if (variant === 'compact') {
    return (
      <div className="post-card post-card-compact">
        <div className="post-card-content">
          <PostAvatar firstPlatform={firstPlatform} profilePicUrl={profilePicUrl} isThreaded={isThreaded} />
          <p>{displayContent}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="post-card">
      {/* Row 1: avatar + timestamp (left) | platform icons (right) */}
      <div className="post-card-header">
        <div className="post-card-meta">
          <PostAvatar firstPlatform={firstPlatform} profilePicUrl={profilePicUrl} isThreaded={isThreaded} />
          {post.createdAt && (
            <span className="post-card-timestamp">{formatRelativeTime(post.createdAt)}</span>
          )}
          {post.hidden && (
            <span className="post-card-status-pill">Hidden</span>
          )}
        </div>
        <div className="post-card-platforms">
          {post.twitter && (
            <a
              href={post.twitter.url}
              target="_blank"
              rel="noopener noreferrer"
              title="View on X/Twitter"
              className="post-card-platform-link"
            >
              <PlatformIcon platformKey="twitter" icon={platforms.twitter.icon} size={16} className="post-card-platform-icon" />
            </a>
          )}
          {post.bluesky && (
            <a
              href={post.bluesky.url}
              target="_blank"
              rel="noopener noreferrer"
              title="View on Bluesky"
              className="post-card-platform-link"
            >
              <PlatformIcon platformKey="bluesky" icon={platforms.bluesky.icon} size={16} className="post-card-platform-icon" />
            </a>
          )}
          {post.farcaster && (
            <a
              href={post.farcaster.url}
              target="_blank"
              rel="noopener noreferrer"
              title="View on Farcaster"
              className="post-card-platform-link"
            >
              <PlatformIcon platformKey="farcaster" icon={platforms.farcaster.icon} size={16} className="post-card-platform-icon" />
            </a>
          )}
        </div>
      </div>

      {/* Row 2: post content */}
      <div className="post-card-body">
        <p>{displayContent}</p>
      </div>

      {/* Row 2.5: image thumbnails */}
      {post.imageUrls && post.imageUrls.length > 0 && (
        <div className="post-card-images">
          {post.imageUrls.map((url, i) => (
            <img
              key={i}
              src={url}
              alt={`Attachment ${i + 1}`}
              className="post-card-image-thumb"
              loading="lazy"
            />
          ))}
        </div>
      )}

      {/* Row 3: action icons — left group + trash right */}
      {showActions && (
        <div className="post-card-footer">
          <div className="post-card-footer-actions">
            {onReply && (
              <button
                className="post-card-action-icon"
                onClick={() => onReply(post)}
                title="Reply"
                aria-label="Reply"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 14 4 9 9 4"></polyline>
                  <path d="M20 20v-7a4 4 0 0 0-4-4H4"></path>
                </svg>
              </button>
            )}
            {onQuote && (
              <button
                className="post-card-action-icon"
                onClick={() => onQuote(post)}
                title="Quote"
                aria-label="Quote"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
              </button>
            )}
            <button
              className={`post-card-action-icon${copied ? ' post-card-action-icon-copied' : ''}`}
              onClick={handleCopy}
              title={copied ? 'Copied!' : 'Copy post text'}
              aria-label={copied ? 'Copied' : 'Copy post text'}
            >
              {copied ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
              )}
            </button>
          </div>
          <button
            className={`post-card-action-icon ${post.hidden ? 'post-card-action-icon-retry' : 'post-card-action-icon-trash'}`}
            onClick={onDelete ? () => onDelete(post) : undefined}
            title={deleteLabel}
            aria-label={deleteLabel}
            disabled={!onDelete}
          >
            {post.hidden ? <RetryIcon /> : <TrashIcon />}
          </button>
        </div>
      )}
    </div>
  );
};
